import React from 'react';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useRollbar } from '@rollbar/react';
import { useRecoilValue } from 'recoil';
import { fetchSelector } from 'common/state/common';
import { useTracking } from 'common/hooks/tracking';
import { TRACKING_EVENTS } from 'common/types/tracking';
import {
  CreateDataExportWebhookPayload,
  CreatedDataExportWebhook,
  DataExportDeliveriesResponse,
  DataExportDelivery,
  DataExportRequestError,
  DataExportWebhook,
  DataExportTokensResponse,
  DataExportWebhooksResponse,
  IssueDataExportTokenPayload,
  IssuedDataExportToken,
} from 'common/types/dataExport';
import { API_URLS } from 'settings/api';
import { processError } from 'utils/helpers';

export const DATA_EXPORT_TOKENS_QUERY_KEY = ['customer-data-export-tokens'];
export const DATA_EXPORT_WEBHOOKS_QUERY_KEY = ['customer-data-export-webhooks'];
/** The mutations whose variables or response hold a credential (an issued token, a webhook secret). */
export const DATA_EXPORT_CREDENTIAL_MUTATION_KEY = ['customer-data-export-credential'];

/**
 * Removes finished credential mutations from React Query's mutation cache. A finished mutation keeps its
 * variables and response for as long as its observer lives (until the component unmounts: `reset()` does not
 * detach it), so callers forget it as soon as the credential is in their own state, or the form is closed.
 */
export const useForgetDataExportCredentials = (): (() => void) => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    const cache = queryClient.getMutationCache();
    cache
      .findAll({ mutationKey: DATA_EXPORT_CREDENTIAL_MUTATION_KEY })
      .filter(mutation => mutation.state.status !== 'loading')
      .forEach(mutation => cache.remove(mutation));
  }, [queryClient]);
};
export const dataExportDeliveriesQueryKey = (webhookId: number) => ['customer-data-export-webhook-deliveries', webhookId];
/** While any delivery is still being sent, its list refreshes this often. */
export const DELIVERIES_REFRESH_INTERVAL_MS = 10000;

/** The deliveries' polling rule: refresh while any of them is still pending, otherwise not at all. */
export const deliveriesRefetchInterval = (data?: DataExportDeliveriesResponse): number | false =>
  data?.deliveries.some(({ status }) => status === 'pending') ? DELIVERIES_REFRESH_INTERVAL_MS : false;

/** Messages the backend returned for a rejected request (422 `{ errors: [...] }`), empty for anything else. */
export const getDataExportErrorMessages = (error: unknown): string[] => {
  const errors = (error as DataExportRequestError | null)?.errors;
  return Array.isArray(errors) ? errors.filter(message => typeof message === 'string' && message.length > 0) : [];
};

/** 4xx responses (no access, unknown id, validation) will not succeed on a retry. */
const retryUnlessClientError = (failureCount: number, error: unknown): boolean => {
  const status = (error as DataExportRequestError | null)?.status;
  const isClientError = typeof status === 'number' && status >= 400 && status < 500;
  return !isClientError && failureCount < 1;
};

const useReportError = () => {
  const rollbar = useRollbar();
  // processError only warns for 401/422 (access and validation), everything else reaches Rollbar.
  return (error: unknown, warningText: string) => processError(error, rollbar.error.bind(rollbar), warningText);
};

export const useDataExportTokens = (): UseQueryResult<DataExportTokensResponse, unknown> => {
  const { fetchGet } = useRecoilValue(fetchSelector);
  const reportError = useReportError();

  return useQuery(
    DATA_EXPORT_TOKENS_QUERY_KEY,
    async () => {
      try {
        return await fetchGet<DataExportTokensResponse>(API_URLS.CUSTOMER_DATA_EXPORT_TOKENS);
      } catch (e: unknown) {
        reportError(e, 'Failed to fetch data export tokens');
        throw e;
      }
    },
    { retry: retryUnlessClientError },
  );
};

export const useDataExportWebhooks = (): UseQueryResult<DataExportWebhooksResponse, unknown> => {
  const { fetchGet } = useRecoilValue(fetchSelector);
  const reportError = useReportError();

  return useQuery(
    DATA_EXPORT_WEBHOOKS_QUERY_KEY,
    async () => {
      try {
        return await fetchGet<DataExportWebhooksResponse>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOKS);
      } catch (e: unknown) {
        reportError(e, 'Failed to fetch data export webhooks');
        throw e;
      }
    },
    { retry: retryUnlessClientError },
  );
};

/**
 * Issues a new download API token. The full token is only in this response: callers must show it
 * straight away and must not persist it anywhere else (it is never sent to tracking or Rollbar).
 */
export const useIssueDataExportToken = (): UseMutationResult<IssuedDataExportToken, unknown, IssueDataExportTokenPayload | void> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationKey: [...DATA_EXPORT_CREDENTIAL_MUTATION_KEY, 'token'],
    mutationFn: (payload: IssueDataExportTokenPayload | void) => {
      // A blank name means no name, as on the backend.
      const name = payload?.name?.trim();
      return fetchPost<IssuedDataExportToken>(API_URLS.CUSTOMER_DATA_EXPORT_TOKENS, name ? { name } : {});
    },
    onSuccess: ({ id, name }) => {
      trackEvent(TRACKING_EVENTS.DATA_EXPORT_TOKEN_ISSUED, { tokenId: id, named: !!name });
    },
    onError: error => reportError(error, 'Failed to issue data export token'),
    // Not awaited: showing the token must not depend on the list refresh finishing.
    onSettled: () => {
      void queryClient.invalidateQueries(DATA_EXPORT_TOKENS_QUERY_KEY);
    },
    // The response holds the token: drop the mutation as soon as nothing observes it (see useForgetDataExportCredentials).
    cacheTime: 0,
  });
};

export const useRevokeDataExportToken = (): UseMutationResult<unknown, unknown, number> => {
  const { fetchDelete } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationFn: (tokenId: number) => fetchDelete(API_URLS.CUSTOMER_DATA_EXPORT_TOKEN(tokenId)),
    onSuccess: (_data, tokenId) => {
      trackEvent(TRACKING_EVENTS.DATA_EXPORT_TOKEN_REVOKED, { tokenId });
    },
    onError: error => reportError(error, 'Failed to revoke data export token'),
    onSettled: () => queryClient.invalidateQueries(DATA_EXPORT_TOKENS_QUERY_KEY),
  });
};

/** Registers a webhook. A generated signing secret is only in this response, see `CreatedDataExportWebhook`. */
export const useCreateDataExportWebhook = (): UseMutationResult<CreatedDataExportWebhook, unknown, CreateDataExportWebhookPayload> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationKey: [...DATA_EXPORT_CREDENTIAL_MUTATION_KEY, 'webhook'],
    mutationFn: (payload: CreateDataExportWebhookPayload) =>
      fetchPost<CreatedDataExportWebhook>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOKS, { ...payload }),
    onSuccess: ({ id, secret }) => {
      trackEvent(TRACKING_EVENTS.DATA_EXPORT_WEBHOOK_REGISTERED, { webhookId: id, secretGenerated: secret !== null });
    },
    onError: error => reportError(error, 'Failed to register data export webhook'),
    // Not awaited: showing a generated secret must not depend on the list refresh finishing.
    onSettled: () => {
      void queryClient.invalidateQueries(DATA_EXPORT_WEBHOOKS_QUERY_KEY);
    },
    // The variables (a supplied secret) and the response (a generated one) are credentials: drop the
    // mutation as soon as nothing observes it.
    cacheTime: 0,
  });
};

/**
 * A webhook's deliveries of the last `retention_days`, newest first. Loaded only while `enabled` (the list is
 * expanded) and refreshed every DELIVERIES_REFRESH_INTERVAL_MS while any of them is still pending.
 */
export const useDataExportWebhookDeliveries = (
  webhookId: number,
  enabled: boolean,
): UseQueryResult<DataExportDeliveriesResponse, unknown> => {
  const { fetchGet } = useRecoilValue(fetchSelector);
  const reportError = useReportError();

  return useQuery(
    dataExportDeliveriesQueryKey(webhookId),
    async () => {
      try {
        return await fetchGet<DataExportDeliveriesResponse>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOK_DELIVERIES(webhookId));
      } catch (e: unknown) {
        reportError(e, 'Failed to fetch data export webhook deliveries');
        throw e;
      }
    },
    {
      enabled,
      retry: retryUnlessClientError,
      refetchInterval: deliveriesRefetchInterval,
    },
  );
};

/** After any change to a webhook's deliveries: its status and last delivery in the list, and its deliveries. */
const useInvalidateWebhook = () => {
  const queryClient = useQueryClient();
  return (webhookId: number) => {
    queryClient.invalidateQueries(DATA_EXPORT_WEBHOOKS_QUERY_KEY);
    queryClient.invalidateQueries(dataExportDeliveriesQueryKey(webhookId));
  };
};

/** Sends a `ping` to the webhook (one attempt, within seconds). */
export const useSendDataExportWebhookTest = (): UseMutationResult<DataExportDelivery, unknown, number> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const invalidateWebhook = useInvalidateWebhook();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationFn: (webhookId: number) => fetchPost<DataExportDelivery>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOK_TEST(webhookId), {}),
    onSuccess: (_delivery, webhookId) => trackEvent(TRACKING_EVENTS.DATA_EXPORT_WEBHOOK_TEST_SENT, { webhookId }),
    onError: error => reportError(error, 'Failed to send a data export webhook test'),
    onSettled: (_data, _error, webhookId) => invalidateWebhook(webhookId),
  });
};

/** Resumes a paused webhook. What failed while it was paused is not resent: redeliver it. */
export const useResumeDataExportWebhook = (): UseMutationResult<DataExportWebhook, unknown, number> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const invalidateWebhook = useInvalidateWebhook();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationFn: (webhookId: number) => fetchPost<DataExportWebhook>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOK_RESUME(webhookId), {}),
    onSuccess: (_webhook, webhookId) => trackEvent(TRACKING_EVENTS.DATA_EXPORT_WEBHOOK_RESUMED, { webhookId }),
    onError: error => reportError(error, 'Failed to resume a data export webhook'),
    onSettled: (_data, _error, webhookId) => invalidateWebhook(webhookId),
  });
};

/** Sends a delivered or failed notification again, with the same delivery id and fresh attempts. */
export const useRedeliverDataExportDelivery = (): UseMutationResult<
  DataExportDelivery,
  unknown,
  { webhookId: number; deliveryId: number }
> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const invalidateWebhook = useInvalidateWebhook();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationFn: ({ webhookId, deliveryId }: { webhookId: number; deliveryId: number }) =>
      fetchPost<DataExportDelivery>(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOK_REDELIVER(webhookId, deliveryId), {}),
    onSuccess: (_delivery, { webhookId, deliveryId }) =>
      trackEvent(TRACKING_EVENTS.DATA_EXPORT_DELIVERY_REDELIVERED, { webhookId, deliveryId }),
    onError: error => reportError(error, 'Failed to redeliver a data export webhook notification'),
    onSettled: (_data, _error, { webhookId }) => invalidateWebhook(webhookId),
  });
};

export const useRemoveDataExportWebhook = (): UseMutationResult<unknown, unknown, number> => {
  const { fetchDelete } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const reportError = useReportError();

  return useMutation({
    mutationFn: (webhookId: number) => fetchDelete(API_URLS.CUSTOMER_DATA_EXPORT_WEBHOOK(webhookId)),
    onSuccess: (_data, webhookId) => {
      trackEvent(TRACKING_EVENTS.DATA_EXPORT_WEBHOOK_REMOVED, { webhookId });
    },
    onError: error => reportError(error, 'Failed to remove data export webhook'),
    onSettled: () => queryClient.invalidateQueries(DATA_EXPORT_WEBHOOKS_QUERY_KEY),
  });
};
